import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useT } from "@/i18n/i18n";
import { SectionHeading } from "@/components/shared";
import { ArrowRight, Flower2, Sprout, ShieldCheck, Link2, Mail, Globe } from "lucide-react";

const SITE = "https://żywiącapolska.com";
const CANONICAL = `${SITE}/konrad-kwiatkowski`;
const PORTRAIT_JPG = "/konrad-kwiatkowski-founder-zywiaca-polska.jpg";
const PORTRAIT_WEBP = "/konrad-kwiatkowski-founder-zywiaca-polska.webp";
const FOUNDER_EMAIL = "contact@zywiacapolska.pl";

function Portrait({ alt }) {
  const [err, setErr] = useState(false);
  if (err) {
    return (
      <div className="grid aspect-[4/5] w-full place-items-center rounded-2xl bg-forestdark" data-testid="founder-portrait-fallback" aria-label={alt} role="img">
        <span className="font-heading text-6xl font-bold text-white/90" style={{ color: "#E1B84B" }}>KK</span>
      </div>
    );
  }
  return (
    <picture>
      <source srcSet={PORTRAIT_WEBP} type="image/webp" />
      <img
        src={PORTRAIT_JPG}
        alt={alt}
        width={640}
        height={800}
        loading="eager"
        fetchPriority="high"
        decoding="async"
        onError={() => setErr(true)}
        data-testid="founder-portrait"
        className="aspect-[4/5] w-full rounded-2xl object-cover"
      />
    </picture>
  );
}

export default function Founder() {
  const { lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);

  const title = L("Konrad Kwiatkowski — Założyciel Żywiącej Polski", "Konrad Kwiatkowski — Founder of Żywiąca Polska");
  const description = L(
    "Konrad Kwiatkowski, założyciel Żywiącej Polski — platformy rozwijającej identyfikowalność polskiej żywności, współpracę rolników i pszczelarzy, zdrowie gleby oraz regionalną odporność żywnościową.",
    "Konrad Kwiatkowski, founder of Żywiąca Polska, a platform focused on Polish food traceability, farmer and beekeeper cooperation, soil health and regional food resilience."
  );
  const alt = L("Konrad Kwiatkowski, założyciel Żywiącej Polski", "Konrad Kwiatkowski, founder of Żywiąca Polska");

  const focus = L(
    ["suwerenność żywnościowa Polski", "identyfikowalność pochodzenia żywności", "współpraca rolników, pszczelarzy i producentów", "zdrowie gleby i odbudowa lokalnych ekosystemów", "zapylanie i rozwój programu Polska Kwitnie", "regionalne łańcuchy dostaw", "uczciwa informacja o pochodzeniu produktów", "narzędzia cyfrowe wspierające polskie gospodarstwa", "długoterminowa odporność polskiego systemu żywnościowego"],
    ["Polish food sovereignty", "food-origin traceability", "farmer, beekeeper and producer coordination", "soil health and local ecosystem restoration", "pollination and Polska Kwitnie development", "regional supply chains", "truthful product-origin information", "digital tools supporting Polish farms", "long-term resilience of Poland's food system"]
  );

  const vision = L(
    ["rolnicy", "sady", "pszczelarze", "przetwórcy", "spółdzielnie", "regionalni nabywcy", "społeczności lokalne", "agronomowie", "przyszłe programy zdrowia gleby", "identyfikowalne polskie produkty żywnościowe"],
    ["farmers", "orchards", "beekeepers", "processors", "cooperatives", "regional buyers", "local communities", "agronomists", "future soil-health programs", "traceable Polish food products"]
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${SITE}/konrad-kwiatkowski#person`,
    name: "Konrad Kwiatkowski",
    url: CANONICAL,
    image: `${SITE}${PORTRAIT_JPG}`,
    jobTitle: L("Założyciel Żywiącej Polski", "Founder of Żywiąca Polska"),
    worksFor: {
      "@type": "Organization",
      "@id": `${SITE}/#organization`,
      name: "Żywiąca Polska",
      url: SITE,
    },
  };

  return (
    <div>
      <Helmet>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:image" content={`${SITE}${PORTRAIT_JPG}`} />
        <meta property="og:locale" content={lang === "pl" ? "pl_PL" : "en_US"} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Identity */}
      <section className="relative isolate overflow-hidden bg-forestdark">
        <div className="zp-hero-overlay absolute inset-0 opacity-60" />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:py-20 lg:grid-cols-[minmax(0,360px)_1fr] lg:items-center">
          <div className="mx-auto w-full max-w-[320px]">
            <Portrait alt={alt} />
          </div>
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium" style={{ color: "#E1B84B" }}>
              {L("Założyciel", "Founder")} · Żywiąca Polska
            </div>
            <h1 className="font-heading text-4xl font-extrabold leading-tight text-white sm:text-5xl">Konrad Kwiatkowski</h1>
            <p className="mt-3 text-lg font-medium text-white/90">{L("Założyciel Żywiącej Polski", "Founder, Żywiąca Polska")}</p>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/85">
              {L(
                "Żywiąca Polska rozwijana jest jako polska platforma rolnictwa, pochodzenia żywności, zapylania, zdrowia gleby, produkcji regionalnej i koordynacji producentów. Rolą założyciela jest wyznaczanie kierunku platformy, jej architektury, zasad oraz długoterminowego rozwoju.",
                "Żywiąca Polska is being developed as a Polish agriculture, food-origin, pollinator, soil-health, regional-production and producer-coordination platform. The founder's role is to define the platform's direction, architecture, principles and long-term development."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Focus areas */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <SectionHeading eyebrow={L("Założyciel", "Founder")} title={L("Obszary pracy założyciela", "Founder focus areas")} />
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="founder-focus-areas">
          {focus.map((item) => (
            <div key={item} className="flex items-start gap-3 rounded-md border border-subtle bg-white p-4">
              <Sprout className="mt-0.5 h-4 w-4 shrink-0 text-forest" aria-hidden="true" />
              <span className="text-sm text-charcoal">{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Doctrine */}
      <section className="border-y border-subtle" style={{ background: "#F6EAD0" }}>
        <div className="mx-auto max-w-4xl px-4 py-14 text-center" data-testid="founder-doctrine">
          <p className="font-heading text-2xl font-bold leading-relaxed text-charcoal sm:text-3xl">
            {L(
              "Polska powinna wiedzieć, kto ją żywi, skąd pochodzi jej żywność i jak wspierać ludzi, którzy ją produkują.",
              "Poland should know who feeds it, where its food comes from, and how to support the people who produce it."
            )}
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-base font-medium" style={{ color: "#8A5A12" }}>
            {L(
              "Technologia ma wzmacniać rolnika, pszczelarza i lokalnego producenta — nie zastępować ich.",
              "Technology should strengthen farmers, beekeepers and local producers — not replace them."
            )}
          </p>
        </div>
      </section>

      {/* Relationship to Polska Kwitnie */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <div className="grid gap-8 lg:grid-cols-2 lg:items-start">
          <div>
            <SectionHeading eyebrow="Polska Kwitnie" title={L("Jeden z pierwszych modułów operacyjnych", "One of the first operational modules")} />
            <p className="mt-6 text-base leading-relaxed text-mutedtext">
              {L(
                "Polska Kwitnie to jeden z pierwszych działających modułów Żywiącej Polski, obejmujący koordynację zapylania, udział pszczelarzy, komunikację o zabiegach, rejestr działań siedliskowych, identyfikowalność partii miodu, przejrzystość polskiego pochodzenia oraz przyszłą regionalną infrastrukturę zapylania.",
                "Polska Kwitnie is one of the first operational modules of Żywiąca Polska, covering pollinator coordination, beekeeper participation, treatment communication, habitat records, honey-batch traceability, Polish-origin transparency and future regional pollinator infrastructure."
              )}
            </p>
            <p className="mt-4 text-xs text-mutedtext">
              {L(
                "Komunikaty o zabiegach są narzędziem informacyjnym, a nie certyfikacją zgodności prawnej. Weryfikacja w aplikacji nie jest równoznaczna z certyfikacją laboratoryjną.",
                "Treatment communication is an information tool, not regulatory certification. In-app verification is not equivalent to laboratory certification."
              )}
            </p>
            <Link to="/polska-kwitnie" data-testid="founder-to-kwitnie" className="mt-6 inline-flex items-center gap-2 rounded-md bg-forest px-4 py-2 text-sm font-semibold text-white hover:bg-forestdark">
              <Flower2 className="h-4 w-4" aria-hidden="true" /> {L("Poznaj Polska Kwitnie", "Explore Polska Kwitnie")}
            </Link>
          </div>

          {/* Broader vision */}
          <div className="rounded-2xl border border-subtle bg-white p-6">
            <div className="flex items-center gap-2 text-sm font-semibold text-forest">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" /> {L("Szersza wizja Żywiącej Polski", "Broader Żywiąca Polska vision")}
            </div>
            <p className="mt-3 text-sm leading-relaxed text-mutedtext">
              {L("Żywiąca Polska ma połączyć:", "Żywiąca Polska is intended to connect:")}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {vision.map((v) => (
                <span key={v} className="rounded-full border border-forest/20 bg-forest/5 px-3 py-1 text-xs font-medium text-forest">{v}</span>
              ))}
            </div>
            <p className="mt-4 text-xs text-mutedtext">
              {L(
                "Platforma koncentruje się na pochodzeniu, zaufaniu, współpracy i odporności — a nie na zastępowaniu istniejących instytucji rolnych.",
                "The platform is positioned around origin, trust, cooperation and resilience — not around replacing existing agricultural institutions."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Official links */}
      <section className="border-t border-subtle bg-secondary/40">
        <div className="mx-auto max-w-4xl px-4 py-14">
          <SectionHeading eyebrow={L("Kontakt", "Contact")} title={L("Kontakt i linki oficjalne", "Contact & official links")} />
          <div className="mt-8 grid gap-3 sm:grid-cols-2" data-testid="founder-links">
            <a href={SITE} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-md border border-subtle bg-white p-4 transition-colors hover:border-forest/40">
              <Globe className="h-5 w-5 text-forest" aria-hidden="true" />
              <span><span className="block text-sm font-semibold text-charcoal">Żywiąca Polska</span><span className="text-xs text-mutedtext">żywiącapolska.com</span></span>
            </a>
            <Link to="/polska-kwitnie" className="flex items-center gap-3 rounded-md border border-subtle bg-white p-4 transition-colors hover:border-forest/40">
              <Flower2 className="h-5 w-5 text-forest" aria-hidden="true" />
              <span><span className="block text-sm font-semibold text-charcoal">Polska Kwitnie</span><span className="text-xs text-mutedtext">/polska-kwitnie</span></span>
            </Link>
            <Link to="/mission" className="flex items-center gap-3 rounded-md border border-subtle bg-white p-4 transition-colors hover:border-forest/40">
              <Link2 className="h-5 w-5 text-forest" aria-hidden="true" />
              <span><span className="block text-sm font-semibold text-charcoal">{L("O projekcie / Misja", "About / Mission")}</span><span className="text-xs text-mutedtext">/mission</span></span>
            </Link>
            <a href={`mailto:${FOUNDER_EMAIL}`} className="flex items-center gap-3 rounded-md border border-subtle bg-white p-4 transition-colors hover:border-forest/40">
              <Mail className="h-5 w-5 text-forest" aria-hidden="true" />
              <span><span className="block text-sm font-semibold text-charcoal">E-mail</span><span className="text-xs text-mutedtext">{FOUNDER_EMAIL}</span></span>
            </a>
          </div>
          <p className="mt-6 text-xs text-mutedtext">
            {L(
              "Konrad Kwiatkowski jest również założycielem HussarAi. Żywiąca Polska i HussarAi pozostają odrębnymi markami i podmiotami.",
              "Konrad Kwiatkowski is also the founder of HussarAi. Żywiąca Polska and HussarAi remain distinct brands and entities."
            )}
          </p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-forest hover:underline">
            <ArrowRight className="h-4 w-4 rotate-180" aria-hidden="true" /> {L("Wróć na stronę główną", "Back to homepage")}
          </Link>
        </div>
      </section>
    </div>
  );
}
